import React, { useEffect } from 'react';
import { Carousel, CarouselItem, EqualHeightProvider, Grid, GridColumn, Notice, Skeleton, SkeletonItem } from 'telia-front-react';
import { TvPackageOfferingCardV2 } from './TvPackageOfferingCardV2';
import { TranslationsContext } from '@telia-common-components/shared';
import { FetchStatus } from '../../../../util/FetchStatus';
import { observer } from 'mobx-react';
import { TELIA_TV_SALVESTUS_E } from '../../util/TvServicesUtil';
import { TvChannelComparison } from '../../channelComparison/TvChannelComparison';
import { useTvPackageStore } from '../../stores/TvpackageStoreProvider';
import { useDidUpdate } from '../../util/hooks/useDidUpdate';
export const TvPackageOfferingCarouselContent = observer((props) => {
    const { tier, language, headingText, isFlow, headingLevel, generateBasketId, basketId, productId, customerId, packageData, productCategoryCode, disableTranslationsStatistics, isTestEnvironment } = props;
    const { translate, translations } = React.useContext(TranslationsContext);
    const findPackageExtraInfo = (title) => packageData.find((pd) => pd.name === title);
    const { tvPackageStore } = useTvPackageStore();
    useEffect(() => {
        if (tvPackageStore) {
            tvPackageStore.fetchTvPackages(basketId, productId, productCategoryCode);
            tvPackageStore.generateBasketIdAndFetchPackagePrices(generateBasketId, customerId);
            tvPackageStore.fetchTvPackageViewRequirements(language);
        }
    }, [basketId, productId, customerId, language, productCategoryCode]);
    useDidUpdate(() => {
        if (basketId) {
            tvPackageStore.fetchPackagePrices(basketId);
        }
    }, [basketId]);
    const getAppAndServiceTitles = (packet) => {
        var _a, _b;
        const servicesFromProps = (_a = findPackageExtraInfo(packet.title)) === null || _a === void 0 ? void 0 : _a.additionalServicesFromProps;
        const appTitles = packet.tvApps.map((tvApp) => tvApp.title);
        const packetServiceTitles = packet.additionalServices.map((service) => service.title);
        const servicesFromPropsTitles = (_b = servicesFromProps === null || servicesFromProps === void 0 ? void 0 : servicesFromProps.map((service) => service.title)) !== null && _b !== void 0 ? _b : [];
        return [...appTitles, ...packetServiceTitles, ...servicesFromPropsTitles];
    };
    const getChannelCount = (packet) => {
        var _a;
        const channelCount = ((_a = findPackageExtraInfo(packet.title)) === null || _a === void 0 ? void 0 : _a.channelsAmount) || packet.channels;
        return `${channelCount}${packet.channels ? '' : '+'}`;
    };
    const getCards = () => {
        if (shouldShowSkeleton()) {
            return getSkeletonItems();
        }
        const orderedTagTop = { text: translate('tvPackageOfferings.ordered.tagTop'), purpose: 'success', size: 'tiny' };
        const packages = tvPackageStore.tvPackageCards
            .filter((p) => { var _a; return findPackageExtraInfo(p.title) && (!productCategoryCode || ((_a = p.categoriesList) === null || _a === void 0 ? void 0 : _a.includes(productCategoryCode))); })
            .sort((a, b) => { var _a, _b; return ((_a = findPackageExtraInfo(a.title)) === null || _a === void 0 ? void 0 : _a.order) - ((_b = findPackageExtraInfo(b.title)) === null || _b === void 0 ? void 0 : _b.order); });
        return packages.map((packet) => {
            var _a, _b, _c, _d;
            const isExistingOffering = packet.title === tvPackageStore.existingPackage;
            const prices = ((_a = tvPackageStore.customerPrices) === null || _a === void 0 ? void 0 : _a.find((price) => price.name === packet.prices.name)) || packet.prices;
            return (React.createElement(TvPackageOfferingCardV2, { tagTop: isExistingOffering ? orderedTagTop : (_b = findPackageExtraInfo(packet.title)) === null || _b === void 0 ? void 0 : _b.tagTop, title: packet.title, headingLevel: headingLevel, channelCount: getChannelCount(packet), viewerCount: packet.viewers, hasRecordedContent: (_c = getAppAndServiceTitles(packet)) === null || _c === void 0 ? void 0 : _c.includes(TELIA_TV_SALVESTUS_E), serviceTitles: getAppAndServiceTitles(packet), prices: prices, openChannelsModal: (newPackage) => {
                    tvPackageStore.isModalOpen = true;
                    tvPackageStore.selectedPackage = newPackage;
                }, serviceOrderUrl: (_d = findPackageExtraInfo(packet.title)) === null || _d === void 0 ? void 0 : _d.url, teliaWebSelfServiceUrl: tvPackageStore.selfServiceUrl, isExistingOffering: isExistingOffering }));
        });
    };
    const getSkeletonItems = () => {
        return [...Array(4).keys()].map((key) => (React.createElement(Skeleton, { key: key, label: translate('skeleton.loading') },
            React.createElement(SkeletonItem, { height: '566px' }))));
    };
    const shouldShowSkeleton = () => {
        return [FetchStatus.FETCHING, FetchStatus.UNINITIALIZED].includes(tvPackageStore.status) || !(translations === null || translations === void 0 ? void 0 : translations.iocc);
    };
    const renderCardGrid = () => {
        const cards = getCards();
        return (React.createElement(Grid, { align: ['start-xs'] }, cards.map((card, index) => (React.createElement(GridColumn, { key: index, width: ['sm-6', 'lg-4', 'xl-3'] }, card)))));
    };
    const renderCardCarousel = () => {
        return (React.createElement(Carousel, { nextArrowText: translate('carousel.next'), prevArrowText: translate('carousel.previous'), arrows: true, hasContainer: true, breakPoint: null, slidesToShow: 1, responsive: [
                {
                    breakPoint: 'sm',
                    props: { slidesToShow: 2, align: 'left' }
                },
                {
                    breakPoint: 'md',
                    props: { slidesToShow: 3, align: 'left' }
                },
                {
                    breakPoint: 'lg',
                    props: { slidesToShow: 4, align: 'left' }
                }
            ] }, getCards().map((card, index) => (React.createElement(CarouselItem, { key: `tv-offer-card-${index}` }, card)))));
    };
    if (tvPackageStore.status === FetchStatus.ERROR) {
        return React.createElement(Notice, { type: 'error' }, translate('general.error.message'));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(TvChannelComparison, { headingLevel: headingLevel, defaultSelectedPackage: tvPackageStore.selectedPackage, onClose: () => (tvPackageStore.isModalOpen = false), isOpen: tvPackageStore.isModalOpen, isModal: true, headingText: headingText, tier: tier, language: language, translations: translations, disableTranslationsStatistics: disableTranslationsStatistics, isTestEnvironment: isTestEnvironment }),
        React.createElement(EqualHeightProvider, { groups: ['packet-title', 'packet-info', 'packet-services', 'packet-prices'] }, isFlow ? renderCardGrid() : renderCardCarousel())));
});
